const PRELOAD_RELOAD_GUARD_KEY = 'rw.preload-reload-guard';
const PRELOAD_RELOAD_GUARD_MS = 60_000;

type ReloadGuard = {
  attemptedAt: number;
  href: string;
};

type DeploymentPreloadRecovery = {
  href: () => string;
  now: () => number;
  reload: () => void;
  storage: Storage;
};

function readReloadGuard(storage: Storage): ReloadGuard | null {
  try {
    const raw = storage.getItem(PRELOAD_RELOAD_GUARD_KEY);
    if (!raw) return null;
    const value: unknown = JSON.parse(raw);
    if (
      typeof value !== 'object' ||
      value === null ||
      !('attemptedAt' in value) ||
      !('href' in value) ||
      typeof value.attemptedAt !== 'number' ||
      typeof value.href !== 'string'
    ) {
      return null;
    }
    return { attemptedAt: value.attemptedAt, href: value.href };
  } catch {
    return null;
  }
}

function persistReloadGuard(storage: Storage, guard: ReloadGuard): boolean {
  try {
    storage.setItem(PRELOAD_RELOAD_GUARD_KEY, JSON.stringify(guard));
    return true;
  } catch {
    return false;
  }
}

export function createDeploymentPreloadErrorHandler({
  href,
  now,
  reload,
  storage,
}: DeploymentPreloadRecovery): (event: Event) => void {
  return (event) => {
    const attemptedAt = now();
    const currentHref = href();
    const previous = readReloadGuard(storage);
    if (
      previous?.href === currentHref &&
      attemptedAt - previous.attemptedAt < PRELOAD_RELOAD_GUARD_MS
    ) {
      return;
    }
    if (!persistReloadGuard(storage, { attemptedAt, href: currentHref })) return;
    event.preventDefault();
    reload();
  };
}

export function installDeploymentPreloadRecovery(): () => void {
  let storage: Storage;
  try {
    storage = window.sessionStorage;
  } catch {
    return () => undefined;
  }
  const handler = createDeploymentPreloadErrorHandler({
    href: () => window.location.href,
    now: () => Date.now(),
    reload: () => window.location.reload(),
    storage,
  });
  window.addEventListener('vite:preloadError', handler);
  return () => window.removeEventListener('vite:preloadError', handler);
}
